import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Hospital, ShieldCheck, Stethoscope, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const FALLBACK_BRANDING = {
  logo_url: "",
  app_name: "MEDIRA",
  app_tagline: "Hospital Management · IPD / OPD",
  signin_kicker: "Sign in",
  signin_title: "Care, charted with clarity.",
  signin_subtitle: "One workspace for OPD, IPD, billing, discharge and NABH-compliant reports — across every hospital you operate.",
  hero_image_url: "",
  hero_kicker: "Built for clinicians",
  hero_title: "From admit card to discharge — one trail, zero paperwork.",
  hero_bullets: [],
  footer_text: "©2026 ApexHMIS | UncodeWeb",
};

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(FALLBACK_BRANDING);

  useEffect(() => {
    api.get("/branding").then((r) => {
      if (r.data && typeof r.data === "object") setBrand({ ...FALLBACK_BRANDING, ...r.data });
    }).catch(() => {});
  }, []);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error || "Login failed");
      return;
    }
    toast.success("Welcome back");
    nav("/");
  };

  const fillDemo = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div
      className="min-h-screen grid grid-cols-1 lg:grid-cols-3"
      style={{ background: "var(--hms-app)" }}
      data-testid="login-page"
    >
      {/* Form (right on desktop, top on mobile) — vertically centered, narrow column */}
      <div className="flex flex-col justify-center items-center p-8 lg:p-10 lg:order-2 lg:col-span-1 relative">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10" data-testid="login-brand">
            <div className="h-10 w-10 grid place-items-center rounded-sm overflow-hidden" style={{ background: "var(--hms-sidebar)" }}>
              {brand.logo_url ? (
                <img src={brand.logo_url} alt="logo" className="h-full w-full object-cover" data-testid="login-logo" />
              ) : (
                <Hospital size={20} weight="fill" className="text-[var(--hms-accent)]" />
              )}
            </div>
            <div>
              <div className="font-heading font-extrabold text-xl leading-none tracking-tight" data-testid="login-app-name">{brand.app_name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--hms-text-2)] mt-1" data-testid="login-app-tagline">
                {brand.app_tagline}
              </div>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.18em] font-heading font-bold text-[var(--hms-accent)] mb-2" data-testid="login-signin-kicker">
            {brand.signin_kicker}
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-1" data-testid="login-signin-title">
            {brand.signin_title}
          </h1>
          <p className="text-sm text-[var(--hms-text-2)] mb-6" data-testid="login-signin-subtitle">
            {brand.signin_subtitle}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">
                Email
              </Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@hospital.com"
                className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">
                Password
              </Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit"
              className="w-full rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)] text-white h-11 font-heading tracking-wide"
            >
              {busy ? "Signing in…" : (<>Sign in <ArrowRight size={16} weight="bold" className="ml-2" /></>)}
            </Button>
          </form>

          {/* Demo accounts block intentionally hidden. Kept in source for
             future re-enable via a superuser-controlled branding flag. */}

          <div className="text-[11px] text-[var(--hms-muted)] mt-8" data-testid="login-footer">
            {brand.footer_text}
          </div>
        </div>
      </div>

      {/* Hero image (left on desktop, hidden on mobile) — 2/3 width */}
      <div className="hidden lg:block relative lg:order-1 lg:col-span-2" style={{ background: "var(--hms-sidebar)" }}>
        {brand.hero_image_url && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-50"
            style={{ backgroundImage: `url(${brand.hero_image_url})` }}
            data-testid="login-hero-image"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B132B] via-[#0B132B]/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="font-heading text-[10px] uppercase tracking-[0.3em] text-white/60 mb-4" data-testid="login-hero-kicker">
            {brand.hero_kicker}
          </div>
          <h2 className="font-heading text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.05] max-w-md" data-testid="login-hero-title">
            {brand.hero_title}
          </h2>
          <ul className="mt-8 space-y-2 text-sm text-white/80 max-w-md" data-testid="login-hero-bullets">
            {(brand.hero_bullets || []).map((b, i) => (
              <li key={i}>· {b}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function DemoBtn({ role, email, pwd, desc, icon: Icon, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left p-3 border border-[var(--hms-border-2)] bg-white rounded-sm hover:shadow-[3px_3px_0_var(--hms-ink)] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all flex items-center gap-3"
    >
      <div className="h-9 w-9 grid place-items-center bg-[var(--hms-paper)] border border-[var(--hms-border)] rounded-sm">
        <Icon size={18} weight="bold" className="text-[var(--hms-ink)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase font-bold tracking-wider text-[var(--hms-text-2)]">{role}</div>
        <div className="text-sm font-semibold truncate">{email}</div>
        <div className="text-[11px] text-[var(--hms-muted)] truncate">{desc}</div>
      </div>
      <div className="font-mono text-[10px] text-[var(--hms-muted)]">{pwd}</div>
    </button>
  );
}
